import {
  accountSearchColumns,
  boPhanSearchColumns,
  chiPhiKhongHopLeSearchColumns,
  dichVuSearchColumns,
  dotThanhToanSearchColumns,
  dvtSearchColumns,
  hopDongSearchColumns,
  kheUocSearchColumns,
  lenhSanXuatSearchColumns,
  loaiTaiSanSearchColumns,
  phiSearchColumns,
  QUERY_KEYS,
  thueSearchColumns,
  vatTuSearchColumns,
  vuViecSearchColumns
} from '@/constants';
import {
  AccountModel,
  BoPhan,
  ChiPhiKhongHopLeData,
  DichVu,
  DonViTinh,
  DotThanhToan,
  HopDong,
  KheUoc,
  LoaiTaiSanCongCu,
  Phi,
  Tax,
  VatTu,
  VuViec
} from '@/types/schemas';
import { CellFieldV2 as CellField, SearchFieldV2 as SearchField } from '@/components/custom/erp';
import type { DataTableRenderCellParams } from '@/components/custom/erp/data-table-v2';
import type { FormFieldState } from '../../../hooks';
import { FormMode } from '@/types/form';

export const getDetailTableColumns = (
  formMode: FormMode,
  state: FormFieldState,
  onCellValueChange: (rowUuid: string, field: string, newValue: MuaHangRecord) => void,
  cellError?: (rowUuid: string, field: string) => string | undefined
) => [
  {
    field: 'ma_dv',
    headerName: 'Mã dịch vụ',
    width: 200,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<DichVu>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.DICH_VU}/`}
        searchColumns={dichVuSearchColumns}
        columnDisplay='ma_dv'
        dialogTitle='Danh mục dịch vụ'
        value={params.row.ma_dv_data?.ma_dv || ''}
        onRowSelection={(row: MuaHangRecord) => {
          onCellValueChange(params.row.uuid, 'ma_dv_data', row);
        }}
        className='w-full'
        disabled={formMode === 'view'}
        error={cellError?.(params.row.uuid, 'ma_dv')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_dv',
      resultField: 'ma_dv_data',
      detailEndpoint: QUERY_KEYS.DICH_VU
    }
  },
  {
    field: 'ten_dv',
    headerName: 'Tên dịch vụ',
    width: 200,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        commitOnBlur
        name='ten_dv'
        type='text'
        value={params.row.ma_dv_data?.ten_dv || ''}
        disabled={formMode === 'view'}
      />
    ),
    nonWritable: true
  },
  {
    field: 'tk_vt',
    headerName: 'Tài khoản nợ',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<AccountModel>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
        searchColumns={accountSearchColumns}
        columnDisplay='code'
        dialogTitle='Danh mục tài khoản'
        value={params.row.tk_vt_data?.code || params.row.ma_dv_data?.tk_cp_data?.code || ''}
        onRowSelection={(row: MuaHangRecord) => {
          onCellValueChange(params.row.uuid, 'tk_vt_data', row);
        }}
        className='w-full'
        disabled={formMode === 'view'}
        error={cellError?.(params.row.uuid, 'tk_vt')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'code',
      resultField: 'tk_vt_data',
      detailEndpoint: QUERY_KEYS.TAI_KHOAN
    }
  },
  {
    field: 'ten_tk',
    headerName: 'Tên tài khoản',
    width: 200,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        commitOnBlur
        name='ten_tk'
        value={params.row.tk_vt_data?.name || params.row.ma_dv_data?.tk_cp_data?.name || ''}
        disabled={formMode === 'view'}
      />
    ),
    nonWritable: true
  },
  {
    field: 'dien_giai',
    headerName: 'Diễn giải/Tên hàng hóa dịch vụ',
    width: 230,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        commitOnBlur
        name='dien_giai'
        type='text'
        value={params.row.dien_giai || params.row.ma_dv_data?.ten_dv || ''}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'dien_giai', newValue)}
        disabled={formMode === 'view'}
      />
    )
  },
  ...(state.ma_gd_code === 'HD6TS'
    ? [
        {
          field: 'ma_lts',
          headerName: 'Loại TS/CC',
          width: 150,
          renderCell: (params: MuaHangRecord) => (
            <SearchField<LoaiTaiSanCongCu>
              type='text'
              searchEndpoint={`/${QUERY_KEYS.LOAI_TAI_SAN_CONG_CU}/`}
              searchColumns={loaiTaiSanSearchColumns}
              columnDisplay='ma_lts'
              dialogTitle='Danh mục loại tài sản/công cụ'
              value={params.row.ma_lts_data?.ma_lts || ''}
              onRowSelection={(row: MuaHangRecord) => {
                onCellValueChange(params.row.uuid, 'ma_lts_data', row);
              }}
              className='w-full'
              disabled={formMode === 'view'}
            />
          ),
          pasteConfig: {
            lookupKey: 'ma_lts',
            resultField: 'ma_lts_data',
            detailEndpoint: QUERY_KEYS.LOAI_TAI_SAN_CONG_CU
          }
        }
      ]
    : []),
  {
    field: 'dvt',
    headerName: 'Đvt',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => {
      return (
        <SearchField<DonViTinh>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.DON_VI_TINH}/`}
          searchColumns={dvtSearchColumns}
          columnDisplay='dvt'
          dialogTitle='Danh mục đơn vị tính'
          value={params.row.dvt_data?.dvt || params.row.ma_dv_data?.dvt_data?.dvt || params.row.dvt || ''}
          onRowSelection={(row: DonViTinh) => {
            onCellValueChange(params.row.uuid, 'dvt_data', row);
          }}
          className='w-full'
          disabled={formMode === 'view'}
          error={cellError?.(params.row.uuid, 'dvt')}
          errorDisplay='tooltip'
        />
      );
    },
    pasteConfig: {
      lookupKey: 'dvt',
      resultField: 'dvt_data',
      detailEndpoint: QUERY_KEYS.DON_VI_TINH
    }
  },
  {
    field: 'so_luong',
    headerName: 'Số lượng',
    width: 100,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        commitOnBlur
        name='so_luong'
        type='number'
        value={params.row.so_luong}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'so_luong', newValue)}
        disabled={formMode === 'view'}
        error={cellError?.(params.row.uuid, 'so_luong')}
        errorDisplay='tooltip'
      />
    )
  },
  {
    field: 'gia_nt',
    headerName: 'Giá VND',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        commitOnBlur
        name='gia_nt'
        type='number'
        value={params.row.gia_nt_manual ? params.row.gia_nt : params.row.ma_dv_data?.gia_nt || params.row.gia_nt || 0}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'gia_nt', newValue)}
        disabled={formMode === 'view'}
      />
    )
  },
  {
    field: 'tien_nt',
    headerName: 'Tiền VND',
    width: 160,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        commitOnBlur
        name='tien_nt'
        type='number'
        value={params.row.tien_nt}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'tien_nt', newValue)}
        disabled={formMode === 'view'}
      />
    )
  },
  ...(state.ck_yn
    ? [
        {
          field: 'tien_tck_nt',
          headerName: 'Tiền trước ck',
          width: 160,
          renderCell: (params: DataTableRenderCellParams) => (
            <CellField
              commitOnBlur
              name='tien_tck_nt'
              type='number'
              value={params.row.tien_tck_nt}
              onValueChange={newValue => onCellValueChange(params.row.uuid, 'tien_tck_nt', newValue)}
              disabled={formMode === 'view'}
            />
          )
        },
        {
          field: 'tl_ck',
          headerName: 'Tỷ lệ chiết khấu',
          width: 120,
          renderCell: (params: DataTableRenderCellParams) => (
            <CellField
              commitOnBlur
              name='tl_ck'
              type='number'
              value={params.row.tl_ck_manual ? params.row.tl_ck || 0 : state.chietKhau || params.row.tl_ck || 0}
              onValueChange={newValue => onCellValueChange(params.row.uuid, 'tl_ck', newValue)}
              disabled={formMode === 'view'}
            />
          )
        },
        {
          field: 'ck_nt',
          headerName: 'Chiết khấu VND',
          width: 140,
          renderCell: (params: DataTableRenderCellParams) => (
            <CellField
              commitOnBlur
              name='ck_nt'
              type='number'
              value={params.row.ck_nt}
              onValueChange={newValue => onCellValueChange(params.row.uuid, 'ck_nt', newValue)}
              disabled={formMode === 'view'}
            />
          )
        }
      ]
    : []),
  {
    field: 'ma_thue',
    headerName: 'Thuế suất',
    width: 170,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<Tax>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.THUE}/`}
        searchColumns={thueSearchColumns}
        columnDisplay='ten_thue'
        dialogTitle='Danh mục thuế'
        value={params.row.ma_thue_data?.ten_thue || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_thue_data', row)}
        className='w-full'
        disabled={formMode === 'view'}
        error={cellError?.(params.row.uuid, 'ma_bp')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_thue',
      resultField: 'ma_thue_data',
      detailEndpoint: QUERY_KEYS.THUE
    }
  },
  {
    field: 'thue_nt',
    headerName: 'Thuế GTGT VND',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        commitOnBlur
        name='thue_nt'
        type='number'
        value={params.row.thue_nt}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'thue_nt', newValue)}
        disabled={formMode === 'view'}
      />
    )
  },
  {
    field: 'ma_bp',
    headerName: 'Bộ phận',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<BoPhan>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.BO_PHAN}/`}
        searchColumns={boPhanSearchColumns}
        columnDisplay='ma_bp'
        dialogTitle='Danh mục bộ phận'
        value={params.row.ma_bp_data?.ma_bp || params.row.ma_dv_data?.ma_bp_data?.ma_bp || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_bp_data', row)}
        className='w-full'
        disabled={formMode === 'view'}
        error={cellError?.(params.row.uuid, 'ma_bp')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_bp',
      resultField: 'ma_bp_data',
      detailEndpoint: QUERY_KEYS.BO_PHAN
    }
  },
  {
    field: 'ma_vv',
    headerName: 'Vụ việc',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<VuViec>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.VU_VIEC}/`}
        searchColumns={vuViecSearchColumns}
        columnDisplay='ma_vu_viec'
        dialogTitle='Danh mục vụ việc'
        value={params.row.ma_vv_data?.ma_vu_viec || params.row.ma_dv_data?.ma_vv_data?.ma_vu_viec || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_vv_data', row)}
        className='w-full'
        disabled={formMode === 'view'}
        error={cellError?.(params.row.uuid, 'ma_vv')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_vu_viec',
      resultField: 'ma_vv_data',
      detailEndpoint: QUERY_KEYS.VU_VIEC
    }
  },
  {
    field: 'ma_hd',
    headerName: 'Hợp đồng',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<HopDong>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.HOP_DONG}/`}
        searchColumns={hopDongSearchColumns}
        columnDisplay='ma_hd'
        dialogTitle='Danh mục hợp đồng'
        value={params.row.ma_hd_data?.ma_hd || params.row.ma_dv_data?.ma_hd_data?.ma_hd || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_hd_data', row)}
        className='w-full'
        disabled={formMode === 'view'}
        error={cellError?.(params.row.uuid, 'ma_hd')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_hd',
      resultField: 'ma_hd_data',
      detailEndpoint: QUERY_KEYS.HOP_DONG
    }
  },
  {
    field: 'ma_dtt',
    headerName: 'Đợt thanh toán',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<DotThanhToan>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.DOT_THANH_TOAN}/`}
        searchColumns={dotThanhToanSearchColumns}
        columnDisplay='ma_dtt'
        dialogTitle='Danh mục đợt thanh toán'
        value={params.row.ma_dtt_data?.ma_dtt || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_dtt_data', row)}
        className='w-full'
        disabled={formMode === 'view'}
        error={cellError?.(params.row.uuid, 'ma_dtt')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_dtt',
      resultField: 'ma_dtt_data',
      detailEndpoint: QUERY_KEYS.DOT_THANH_TOAN
    }
  },
  {
    field: 'ma_ku',
    headerName: 'Khế ước',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<KheUoc>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.KHE_UOC}/`}
        searchColumns={kheUocSearchColumns}
        columnDisplay='ma_ku'
        dialogTitle='Danh mục khế ước'
        value={params.row.ma_ku_data?.ma_ku || params.row.ma_dv_data?.ma_ku_data?.ma_ku || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_ku_data', row)}
        className='w-full'
        disabled={formMode === 'view'}
        error={cellError?.(params.row.uuid, 'ma_ku')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_ku',
      resultField: 'ma_ku_data',
      detailEndpoint: QUERY_KEYS.KHE_UOC
    }
  },
  {
    field: 'ma_phi',
    headerName: 'Phí',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<Phi>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.PHI}/`}
        searchColumns={phiSearchColumns}
        columnDisplay='ma_phi'
        dialogTitle='Danh mục phí'
        value={params.row.ma_phi_data?.ma_phi || params.row.ma_dv_data?.ma_phi_data?.ma_phi || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_phi_data', row)}
        className='w-full'
        disabled={formMode === 'view'}
        error={cellError?.(params.row.uuid, 'ma_phi')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_phi',
      resultField: 'ma_phi_data',
      detailEndpoint: QUERY_KEYS.PHI
    }
  },
  {
    field: 'ma_sp',
    headerName: 'Sản phẩm',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<VatTu>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.VAT_TU}/`}
        searchColumns={vatTuSearchColumns}
        columnDisplay='ma_vt'
        dialogTitle='Danh mục sản phẩm'
        value={params.row.ma_sp_data?.ma_vt || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_sp_data', row)}
        className='w-full'
        disabled={formMode === 'view'}
        error={cellError?.(params.row.uuid, 'ma_sp')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_vt',
      resultField: 'ma_sp_data',
      detailEndpoint: QUERY_KEYS.VAT_TU
    }
  },
  {
    field: 'ma_lsx',
    headerName: 'Lệnh sản xuất',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<MuaHangRecord>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.LENH_SAN_XUAT}`}
        searchColumns={lenhSanXuatSearchColumns}
        columnDisplay='ma_lsx'
        dialogTitle='Danh mục lệnh sản xuất'
        value={params.row.ma_lsx_data?.ma_lsx || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_lsx_data', row)}
        className='w-full'
        disabled={formMode === 'view'}
        error={cellError?.(params.row.uuid, 'ma_lsx')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_lsx',
      resultField: 'ma_lsx_data',
      detailEndpoint: QUERY_KEYS.LENH_SAN_XUAT
    }
  },
  {
    field: 'ma_cp0',
    headerName: 'C/p không h/lệ',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<ChiPhiKhongHopLeData>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.CHI_PHI_KHONG_HOP_LE}/`}
        searchColumns={chiPhiKhongHopLeSearchColumns}
        columnDisplay='ma_cp_khl'
        dialogTitle='Danh mục chi phí không hợp lệ'
        value={params.row.ma_cp0_data?.ma_cp || params.row.ma_cp0_data?.ma_cp_khl || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_cp0_data', row)}
        className='w-full'
        disabled={formMode === 'view'}
        error={cellError?.(params.row.uuid, 'ma_cp0')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_cp_khl',
      resultField: 'ma_cp0_data',
      detailEndpoint: QUERY_KEYS.CHI_PHI_KHONG_HOP_LE
    }
  }
];
